import { SvgIconProps } from '@mui/material';
import { BenefitDTO, BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { ExtensionInterface } from 'probonio-shared-ui/registry/extension-definition';
import React from 'react';
import { getExtensionForBenefit } from '../../module/benefit';

export interface BenefitListItemProps {
  icon: React.ComponentType<SvgIconProps>;
  title?: string;
  titleKey: string;
  picture: string;
  navigateTo: string;
  benefitProgress: number;
  descriptionKey?: string;
  descriptionValues?: object;
  badgeValue?: number | string;
  badgeValueKey?: string;
}

export abstract class BenefitListItemExtensionInterface extends ExtensionInterface {
  /**
   * Determines if this benefit should be hidden from the benefit list
   * @param activeBenefit - The current benefit data
   * @param activeBenefits - All active benefits (for cross-benefit filtering)
   * @param context - Optional context data for benefit-specific logic
   * @returns true if the benefit should be hidden
   */
  abstract isHidden(activeBenefit: BenefitDTO, activeBenefits: BenefitDTO[], context?: Record<string, unknown>): boolean;

  /**
   * Gets the props for rendering the benefit list item
   * @param activeBenefit - The current benefit data
   * @param activeBenefits - All active benefits
   * @returns The props for the benefit item component or null if it should render custom content
   */
  abstract getListItemProps(activeBenefit: BenefitDTO, activeBenefits: BenefitDTO[]): BenefitListItemProps | null;

  /**
   * Renders custom content for the benefit (optional)
   * Used for complex benefits like Flex that need custom rendering
   * @param activeBenefit - The current benefit data
   * @param activeBenefits - All active benefits
   * @returns Custom React content or null to use standard rendering
   */
  renderCustomContent?(activeBenefit: BenefitDTO, activeBenefits: BenefitDTO[]): React.ReactNode | null;

  /**
   * Gets a unique key for this benefit item (important for custom benefits)
   * @param activeBenefit - The current benefit data
   * @returns A unique key string
   */
  getKey(activeBenefit: BenefitDTO): string {
    return activeBenefit.benefit;
  }
}

const benefitListItemCache = new Map<BenefitDTOBenefitEnum, BenefitListItemExtensionInterface>();

/**
 * Retrieves the benefit list item extension for a specific benefit type.
 * Uses caching to avoid redundant EXTENSIONS lookups.
 *
 * @param benefit - The benefit enum value (e.g., BenefitDTOBenefitEnum.Lunch)
 * @returns The matched benefit list item extension, or undefined if none exist
 */
export function getBenefitListItemExtension(benefit: BenefitDTOBenefitEnum): BenefitListItemExtensionInterface | undefined {
  if (benefitListItemCache.has(benefit)) {
    return benefitListItemCache.get(benefit);
  }

  const benefitListItemExtension = getExtensionForBenefit(benefit, BenefitListItemExtensionInterface);

  if (!benefitListItemExtension) {
    return undefined;
  }

  benefitListItemCache.set(benefit, benefitListItemExtension);

  return benefitListItemExtension;
}
